import { createTheme } from '@mui/material/styles';

import type {} from '@mui/lab/themeAugmentation';
import type { ColorSystemOptions, CSSObject, Theme } from '@mui/material/styles';
import type {} from '@mui/material/themeCssVarsAugmentation';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import type { CSSProperties } from 'react';

export const colorSchemeSelector = '[data-color-scheme="%s"]';

const spacing = (val: number) => `${0.25 * val}rem`; // Matches Tailwind's spacing scale
const color = (val: string) => `var(--radix-${val})`;

const inputPaddingX = spacing(2);
const inputPaddingY = spacing(1);
const buttonPaddingX = spacing(2);
const buttonPaddingY = spacing(1.25);
const translucentPaperStyles = {
	backgroundColor: `color-mix(in oklab, ${color('gray-1')}, transparent 30%)`,
	backdropFilter: 'blur(8px)',
};
export const dataGridRowHoverColor = `color-mix(in oklab, var(--mui-palette-background-default), ${color('blue-3')} 30%)`;

const colorSystemOptions: ColorSystemOptions = {
	palette: {
		// Built-in colours:
		action: {
			active: color('gray-a12'),
			disabled: color('gray-a5'),
			focus: color('blue-a3'),
			hover: color('gray-a3'),
			selectedOpacity: 1,
		},
		background: {
			default: color('background'),
			paper: color('gray-2'),
		},
		divider: color('gray-a6'),
		error: {
			main: color('red-9'),
			contrastText: color('red-contrast'),
		},
		warning: {
			main: color('amber-9'),
		},
		orange: {
			main: color('orange-a9'),
			contrastText: color('orange-contrast'),
		},
		gray: {
			main: color('gray-a11'),
			contrastText: color('gray-contrast'),
		},
		primary: {
			main: color('blue-8'),
			dark: color('blue-7'),
			contrastText: color('blue-contrast'),
		},
		text: {
			primary: color('gray-12'),
			secondary: color('gray-a9'),
			disabled: color('gray-a5'),
		},

		// Component-specific colours:
		Alert: {
			warningColor: color('amber-a12'),
		},
		DataGrid: {
			bg: color('background'),
		},
		TableCell: {
			border: color('gray-a5'),
		},
		Tooltip: {
			bg: translucentPaperStyles.backgroundColor,
		},
	},
};

export const theme = createTheme({
	modularCssLayers: '@layer base, mui, theme, components, utilities;',
	cssVariables: {
		// This will cause MUI to generate CSS variables for the palette by colour scheme under the `data-mode-` attribute.
		// So if you put the `data-color-scheme="dark"` attribute on any HTML element, everything within it will use the
		// dark colour scheme. By default, the below option is set to 'media', which means MUI will use the CSS
		// `@media(prefers-color-scheme)` query to determine the colour scheme.
		// MUI automatically puts a `data-mode-` attribute on the `<html>` element based on the current colour scheme.
		colorSchemeSelector,

		nativeColor: true,
	},

	colorSchemes: {
		// 'dark' and 'light' colour schemes are automatically merged with MUI's default dark and light schemes.
		// Both colour schemes use the exact same tokens because the CSS variables are dynamic.
		dark: colorSystemOptions,
		light: colorSystemOptions,
	},

	opacity: {
		inputPlaceholder: 1,
	},

	shape: {
		borderRadius: 'var(--radius-md)' as unknown as number, // For Tailwind class detection: rounded-md
	},

	spacing,

	typography: {
		fontFamily: 'var(--font-sans)',

		h1: undefined,
		h2: undefined,
		h3: undefined,
		h4: undefined,
		h5: undefined,
		h6: undefined,
		subtitle1: undefined,
		subtitle2: undefined,
		body1: undefined,
		body2: undefined,
		button: undefined,
		caption: undefined,
		overline: undefined,

		'2xs': {
			fontSize: 'var(--text-2xs)',
		},
		xs: {
			fontSize: 'var(--text-xs)',
		},
		sm: {
			fontSize: 'var(--text-sm)',
		},
		base: {
			fontSize: 'var(--text-base)',
		},
		lg: {
			fontSize: 'var(--text-lg)',
		},
		xl: {
			fontSize: 'var(--text-xl)',
		},
		'2xl': {
			fontSize: 'var(--text-2xl)',
		},
		'3xl': {
			fontSize: 'var(--text-3xl)',
		},
		'4xl': {
			fontSize: 'var(--text-4xl)',
		},
	},

	components: {
		MuiAccordion: {
			styleOverrides: {
				root: {
					borderBlockWidth: '0px',

					'&:first-of-type': {
						borderTopWidth: '1px',
					},
					'&:last-of-type': {
						borderBottomWidth: '1px',
					},
				},
			},
		},

		MuiAccordionSummary: {
			styleOverrides: {
				root: {
					minHeight: '1lh',

					'&.Mui-expanded': {
						minHeight: '1lh',
					},
				},

				content: {
					margin: `${spacing(2)} ${spacing(0)}`,

					'&.Mui-expanded': {
						margin: `${spacing(2)} ${spacing(0)}`,
					},
				},
			},
		},

		MuiAlert: {
			styleOverrides: {
				root: ({ theme }) => ({
					alignItems: 'center',
					padding: `${spacing(1)} ${spacing(2)}`,

					variants: [
						{
							props: { severity: 'error' },
							style: {
								borderColor: color('red-a3'),
								backgroundColor: `oklch(from ${color('red-3')} l c h / 0.5)`,

								...theme.applyStyles('light', {
									borderColor: color('red-a6'),
									backgroundColor: `oklch(from ${color('red-3')} l c h / 0.9)`,
								}),
							},
						},
						{
							props: { severity: 'success' },
							style: {
								borderColor: color('green-a5'),
								backgroundColor: `oklch(from ${color('green-3')} l c h / 0.9)`,
							},
						},
						{
							props: { severity: 'warning' },
							style: {
								borderColor: color('amber-a6'),
								backgroundColor: `oklch(from ${color('amber-3')} calc(l * 1.05) calc(c * 0.6) h / 0.9)`,
							},
						},
						{
							props: { severity: 'info' },
							style: {
								borderColor: color('blue-a4'),
								backgroundColor: `oklch(from ${color('blue-3')} l c h / 0.9)`,
							},
						},
					],
				}),

				action: {
					marginRight: '0rem',
					paddingTop: '0rem',
					flexShrink: 0,
				},

				icon: {
					padding: '0rem',
					marginRight: spacing(2),
					fontSize: 'var(--text-xl)',

					variants: [
						{
							props: { severity: 'warning' },
							style: {
								color: `oklch(from ${color('amber-11')} calc(l * 1.05) c h / 0.9)`,
							},
						},

						{
							props: { severity: 'error' },
							style: {
								color: `oklch(from ${color('red-9')} calc(l*1.15) c h)`,
							},
						},
					],
				},

				message: {
					padding: `${spacing(1.5)} 0rem`,
					minHeight: '0rem',
					alignSelf: 'stretch',
					flexGrow: 1,

					variants: [
						{
							props: { severity: 'error' },
							style: {
								color: color('red-12'),
							},
						},
					],
				},
			},
		},

		MuiAutocomplete: {
			defaultProps: {
				renderOption: ({ key, ...props }, option, state, ownerState) => (
					<li {...props} key={key as string}>
						<p>{ownerState.getOptionLabel(option)}</p>
					</li>
				),
			},
			styleOverrides: {
				endAdornment: {
					display: 'flex',
					gap: spacing(0.5),
					marginRight: spacing(-1),
				},
				input: {
					paddingTop: '0rem !important',
					paddingBottom: '0rem !important',
				},
				inputRoot: {
					padding: spacing(1),

					'.MuiAutocomplete-hasClearIcon :is(&)': {
						paddingRight: `${spacing(14)} !important`,
					},

					// Targets `<Autocomplete multiple />`
					'&.MuiInputBase-adornedStart': {
						paddingTop: `${spacing(1)} !important`,
						paddingBottom: `${spacing(1)} !important`,
						paddingLeft: `${spacing(1)} !important`,

						'.MuiDataGrid-cell &': {
							paddingTop: `${spacing(0.5)} !important`,
							paddingBottom: `${spacing(0.5)} !important`,
						},
					},
				},
				listbox: {
					paddingTop: spacing(1),
					paddingBottom: spacing(1),
				},
				option: {
					padding: `${spacing(2.5)} ${spacing(3)}`,
					transition: 'background-color 50ms',

					'&:is(:hover, .Mui-focused)': {
						backgroundColor: `oklch(from ${color('gray-10')} l c h / 0.05)`,
					},

					'&[aria-selected="true"]': {
						backgroundColor: `oklch(from ${color('blue-10')} l c h / 0.1)`,
						color: color('blue-11'),

						'&:is(:hover, .Mui-focused)': {
							backgroundColor: `oklch(from ${color('blue-10')} l c h / 0.15)`,
						},
					},
				},
				paper: {
					backgroundColor: translucentPaperStyles.backgroundColor,
					backdropFilter: translucentPaperStyles.backdropFilter,
				},
				tag: {
					margin: spacing(0.75),

					'.MuiDataGrid-cell &': {
						margin: `${spacing(0.25)} ${spacing(0.5)}`,
					},
				},

				clearIndicator: {
					padding: spacing(1),
					margin: '0rem',
				},
				popupIndicator: {
					padding: spacing(1),
					margin: '0rem',
				},
			},
		},

		MuiBadge: {
			styleOverrides: {
				badge: {
					fontSize: 'var(--text-xs)',
				},
			},
		},

		MuiBreadcrumbs: {
			styleOverrides: {
				separator: {
					display: 'revert',
				},
			},
		},

		MuiButton: {
			defaultProps: {
				variant: 'contained',
			},
			styleOverrides: {
				root: ({ theme }) => ({
					textTransform: 'revert',
					minHeight: '1cap',
					fontSize: 'var(--text-base)',
					minWidth: '0px',
					flexShrink: 0,
					padding: `${buttonPaddingY} ${buttonPaddingX}`,
					transitionDuration: '50ms',
					'--icon-margin': spacing(1),

					'& > .MuiButton-icon': {
						height: '0px', // Make the icon not contribute to the button's height
						alignItems: 'center',

						'& > svg.MuiSvgIcon-root': {
							fontSize: 'inherit',
						},
					},

					variants: [
						{
							props: { variant: 'contained' },
							style: {
								color: color('gray-a12'),
								textShadow: '1px 1px 0px var(--text-shadow-color)',
								['--text-shadow-color']: `oklch(from ${color('gray-3')} l c h / 0.7)`,

								'&.Mui-disabled': {
									border: `1px solid ${color('gray-a2')}`,
									textShadow: 'none',
								},

								'&.MuiButton-loading': {
									color: color('gray-a5'),
								},

								'& .MuiSvgIcon-root': {
									filter: 'drop-shadow(1px 1px 0px var(--text-shadow-color))',
								},

								...theme.applyStyles('light', {
									color: color('gray-1'),
									['--text-shadow-color']: `oklch(from ${color('gray-12')} l c h / 0.3)`,
									// Drop the Material elevation shadow in light mode so buttons sit flat.
									boxShadow: 'none',
									'&:hover': { boxShadow: 'none' },
									'&:active': { boxShadow: 'none' },

									'&.Mui-disabled': {
										border: `1px solid ${color('gray-a4')}`,
										color: color('gray-a7'),
									},
								}),
							},
						},
						{
							props: { variant: 'contained', color: 'primary' },
							style: {
								backgroundColor: color('blue-7'),
								border: `1px solid ${color('blue-a5')}`,
								['--button-shadow-color']: `oklch(from ${color('blue-9')} l c h / 0.5)`,

								'&:hover': {
									backgroundColor: color('blue-8'),
								},

								'&.Mui-disabled': {
									backgroundColor: `oklch(from ${color('blue-a3')} calc(l * 0.8) calc(c * 0.6) h)`,
									border: `1px solid ${color('blue-a2')}`,
								},

								...theme.applyStyles('light', {
									backgroundColor: color('blue-8'),
									border: `1px solid ${color('blue-a7')}`,

									'&:hover': {
										backgroundColor: color('blue-9'),
									},
								}),
							},
						},
						{
							props: { variant: 'contained', color: 'success' },
							style: {
								backgroundColor: color('green-a6'),
								border: `1px solid ${color('green-a3')}`,
								['--button-shadow-color']: `oklch(from ${color('green-9')} l c h / 0.4)`,

								'&:hover': {
									backgroundColor: color('green-7'),
								},

								'&.Mui-disabled': {
									backgroundColor: `oklch(from ${color('green-a3')} calc(l * 0.8) calc(c * 0.4) h)`,
									border: `1px solid ${color('green-a2')}`,
								},

								...theme.applyStyles('light', {
									backgroundColor: color('green-8'),
									border: `1px solid ${color('green-a7')}`,

									'&:hover': {
										backgroundColor: `oklch(from ${color('green-8')} calc(l * 0.94) c h)`,
									},
								}),
							},
						},
						{
							props: { variant: 'contained', color: 'warning' },
							style: {
								backgroundColor: `oklch(from ${color('yellow-11')} calc(l * 0.65) c h / 0.8)`,
								border: `1px solid ${color('yellow-a6')}`,
								['--button-shadow-color']: `oklch(from ${color('yellow-9')} l c h / 0.3)`,

								'&:hover': {
									backgroundColor: `oklch(from ${color('yellow-9')} l c h / 0.5)`,
								},

								'&.Mui-disabled': {
									backgroundColor: color('yellow-a3'),
									border: `1px solid ${color('yellow-a2')}`,
								},

								...theme.applyStyles('light', {
									backgroundColor: `oklch(from ${color('yellow-9')} l calc(c * 0.8) h)`,
									color: `oklch(from ${color('yellow-a12')} l c h / 0.9)`,
									border: `1px solid ${color('yellow-a6')}`,
									['--text-shadow-color']: `oklch(from ${color('gray-2')} l c h / 0.7)`,

									'&:hover': {
										backgroundColor: `oklch(from ${color('yellow-9')} calc(l * 0.97) c h)`,
									},
								}),
							},
						},
						{
							props: { variant: 'contained', color: 'error' },
							style: {
								backgroundColor: color('red-a4'),
								border: `1px solid ${color('red-a3')}`,
								['--button-shadow-color']: `oklch(from ${color('red-9')} l c h / 0.6)`,

								'&:hover': {
									backgroundColor: color('red-a6'),
								},

								'&.Mui-disabled': {
									backgroundColor: color('red-a2'),
									border: `1px solid ${color('red-a1')}`,
								},

								...theme.applyStyles('light', {
									backgroundColor: `oklch(from ${color('red-9')} calc(l * 1.15) c h)`,
									color: `oklch(from ${color('red-1')} l c h / 0.95)`,
									border: `1px solid ${color('red-a10')}`,

									'&:hover': {
										backgroundColor: `oklch(from ${color('red-9')} calc(l * 1.08) c h)`,
									},
								}),
							},
						},
						{
							props: { variant: 'contained', color: 'gray' },
							style: {
								backgroundColor: color('gray-a7'),
								border: `1px solid ${color('gray-a4')}`,
								['--button-shadow-color']: `oklch(from ${color('gray-9')} l c h / 0.4)`,

								'&:hover': {
									backgroundColor: `oklch(from ${color('gray-a8')} calc(l * 0.9) c h)`,
								},

								'&.Mui-disabled': {
									backgroundColor: color('gray-a3'),
									border: `1px solid ${color('gray-a2')}`,
								},

								...theme.applyStyles('light', {
									backgroundColor: `oklch(from ${color('gray-9')} calc(l * 1.15) c h)`,
									border: `1px solid ${color('gray-a7')}`,
									['--text-shadow-color']: `color(from ${color('gray-12')} display-p3 r g b / 0.5)`,

									'&:hover': {
										backgroundColor: color('gray-a9'),
									},
								}),
							},
						},
						{
							props: { variant: 'contained', color: 'orange' },
							style: {
								backgroundColor: `oklch(from ${color('orange-9')} l c h / 0.5)`,
								border: `1px solid ${color('orange-a5')}`,
								['--button-shadow-color']: `oklch(from ${color('orange-9')} l c h / 0.4)`,

								'&:hover': {
									backgroundColor: `oklch(from ${color('orange-9')} l c h / 0.6)`,
								},

								'&.Mui-disabled': {
									backgroundColor: color('orange-a3'),
									border: `1px solid ${color('orange-a2')}`,
								},

								...theme.applyStyles('light', {
									backgroundColor: `oklch(from ${color('orange-10')} calc(l * 1.08) calc(c * 0.8) h)`,
									border: `1px solid ${color('orange-a10')}`,

									'&:hover': {
										backgroundColor: `oklch(from ${color('orange-9')} calc(l * 1.04) c h)`,
									},
								}),
							},
						},

						{
							props: { variant: 'outlined' },
							style: {
								textShadow: '1px 1px 0px var(--text-shadow-color)',
								['--text-shadow-color']: `oklch(from ${color('gray-1')} l c h / 0.95)`,

								...theme.applyStyles('light', {
									['--text-shadow-color']: `oklch(from ${color('gray-1')} l c h / 0.8)`,
								}),
							},
						},
						{
							props: { variant: 'outlined', color: 'primary' },
							style: {
								backgroundColor: `oklch(from ${color('blue-3')} calc(l * 1.02) calc(c * 0.7) h / 0.9)`,
								color: color('blue-11'),
								borderColor: color('blue-a6'),
								['--button-shadow-color']: `oklch(from ${color('blue-9')} l c h / 0.5)`,

								'&:hover': {
									backgroundColor: color('blue-a3'),
									borderColor: color('blue-a7'),
								},

								'&.Mui-disabled': {
									color: `oklch(from ${color('blue-a5')} l calc(c * 0.7) h)`,
									borderColor: `oklch(from ${color('blue-a3')} l calc(c * 0.8) h)`,
								},

								...theme.applyStyles('light', {
									'&:hover': {
										backgroundColor: `oklch(from ${color('blue-8')} l c h / 0.1)`,
										borderColor: color('blue-8'),
									},
								}),
							},
						},
						{
							props: { variant: 'outlined', color: 'success' },
							style: {
								backgroundColor: color('green-a2'),
								color: color('green-11'),
								borderColor: color('green-a7'),
								['--button-shadow-color']: `oklch(from ${color('green-9')} l c h / 0.4)`,

								'&:hover': {
									backgroundColor: color('green-a3'),
									borderColor: color('green-a8'),
								},

								'&.Mui-disabled': {
									color: `oklch(from ${color('green-a5')} l calc(c * 0.7) h)`,
									borderColor: `oklch(from ${color('green-a3')} l calc(c * 0.8) h)`,
								},
							},
						},
						{
							props: { variant: 'outlined', color: 'warning' },
							style: {
								backgroundColor: `oklch(from ${color('yellow-a3')} l c h / calc(alpha * 0.8))`,
								color: color('yellow-11'),
								borderColor: color('yellow-a6'),
								['--button-shadow-color']: `oklch(from ${color('yellow-9')} l c h / 0.3)`,

								'&:hover': {
									backgroundColor: color('yellow-a4'),
									borderColor: color('yellow-a7'),
								},

								'&.Mui-disabled': {
									color: `oklch(from ${color('yellow-a5')} l calc(c * 0.7) h)`,
									borderColor: `oklch(from ${color('yellow-a3')} l calc(c * 0.8) h)`,
								},

								...theme.applyStyles('light', {
									color: color('yellow-12'),
									borderColor: color('yellow-a7'),
								}),
							},
						},
						{
							props: { variant: 'outlined', color: 'error' },
							style: {
								backgroundColor: color('red-a2'),
								color: `oklch(from ${color('red-a12')} calc(l * 0.85) calc(c * 2.5) h / alpha)`,
								borderColor: `oklch(from ${color('red-a6')} l c h / calc(alpha * 0.9))`,
								['--button-shadow-color']: `oklch(from ${color('red-9')} l c h / 0.6)`,

								'&:hover': {
									backgroundColor: `oklch(from ${color('red-a3')} l c h / calc(alpha * 1))`,
									borderColor: color('red-7'),
								},

								'&.Mui-disabled': {
									backgroundColor: color('red-a1'),
									color: `oklch(from ${color('red-a4')} l calc(c * 0.6) h)`,
									borderColor: `oklch(from ${color('red-a2')} l calc(c * 0.8) h)`,
								},

								...theme.applyStyles('light', {
									backgroundColor: `oklch(from ${color('red-5')} l c h / 0.3)`,
									borderColor: `oklch(from ${color('red-a8')} l c h / calc(alpha * 0.9))`,

									'&:hover': {
										backgroundColor: `oklch(from ${color('red-6')} l c h / 0.6)`,
										borderColor: `oklch(from ${color('red-9')} calc(l * 1.1) c h)`,
									},
								}),
							},
						},
						{
							props: { variant: 'outlined', color: 'gray' },
							style: {
								backgroundColor: `oklch(from ${color('gray-6')} l c h / 0.6)`,
								color: `oklch(from ${color('gray-a12')} l c h / calc(alpha * 0.8))`,
								borderColor: color('gray-a6'),
								['--button-shadow-color']: `oklch(from ${color('gray-9')} l c h / 0.4)`,

								'&:hover': {
									backgroundColor: color('gray-a5'),
									borderColor: color('gray-a8'),
								},

								'&.Mui-disabled': {
									backgroundColor: color('gray-a2'),
									color: color('gray-a6'),
									borderColor: color('gray-a2'),
								},

								...theme.applyStyles('light', {
									backgroundColor: `oklch(from ${color('gray-4')} calc(l * 1.01) c h / 0.7)`,
									borderColor: color('gray-a7'),
								}),
							},
						},
						{
							props: { variant: 'outlined', color: 'orange' },
							style: {
								backgroundColor: color('orange-a3'),
								color: color('orange-a11'),
								borderColor: color('orange-a6'),
								['--button-shadow-color']: `oklch(from ${color('orange-9')} l c h / 0.4)`,

								'&:hover': {
									backgroundColor: color('orange-a4'),
									borderColor: color('orange-a7'),
								},

								'&.Mui-disabled': {
									backgroundColor: `color(from ${color('orange-a3')} display-p3 r g b / calc(alpha * 0.4))`,
									color: `oklch(from ${color('orange-a5')} l calc(c * 0.6) h)`,
									borderColor: color('orange-a3'),
								},

								...theme.applyStyles('light', {
									'&:hover': {
										backgroundColor: `oklch(from ${color('orange-a4')} l c h / calc(alpha * 0.8))`,
										borderColor: color('orange-a8'),
									},
								}),
							},
						},

						{
							props: { disabled: true },
							style: {
								boxShadow: 'none',
							},
						},
					],
				}),

				text: {
					margin: spacing(-2),

					'&:hover': {
						backgroundColor: color('blue-a3'),
					},
				},

				endIcon: {
					marginLeft: 'var(--icon-margin)',
				},
				startIcon: {
					marginRight: 'var(--icon-margin)',
				},
			},
		},

		MuiButtonGroup: {
			styleOverrides: {
				firstButton: {
					borderRightColor: 'transparent !important',
				},
				middleButton: {
					borderRightColor: 'transparent !important',
				},
			},
		},

		MuiCheckbox: {
			styleOverrides: {
				root: {
					padding: spacing(2),
					margin: spacing(-2),

					'&:hover': {
						backgroundColor: color('gray-a4'),
					},
				},
			},
		},

		MuiChip: {
			defaultProps: {
				color: 'primary',
			},
			styleOverrides: {
				root: ({ theme }) => ({
					fontSize: 'var(--text-sm)',
					// spacing(1) padding on top and bottom = spacing(2) total padding
					// 2px for the 1px border on top and bottom
					height: `calc(1cap + ${spacing(2)} + 2px)`,
					minWidth: `calc(1cap + ${spacing(2)} + 2px)`, // Ensure it's never narrower than it is tall
					justifyContent: 'center',
					alignItems: 'center',
					gap: '1px',
					verticalAlign: 'baseline',
					transitionDuration: '50ms',
					flexShrink: 0,

					/*
					 * Makes the chip's baseline the actual baseline of the text within, rather than simply the bottom edge of the
					 * icon SVG within.
					 *
					 * A flex container takes its baseline from its first flex item, which for a chip with an `icon` is the icon
					 * — and an icon has no text of its own, so its baseline is synthesized at its bottom edge, a few pixels below
					 * the label's. This zero-width item is first instead, so it supplies the baseline: `alignItems` above centres
					 * it exactly as it does the label, and `1cap` matches the height of the label's cap-trimmed text box, putting
					 * its bottom edge on the label's baseline. The negative margin cancels the `gap` it would otherwise add.
					 */
					'&::before': {
						content: '""',
						width: 0,
						height: '1cap',
						marginRight: '-1px',
					},

					variants: [
						{
							props: { variant: 'filled' },
							style: {
								color: color('gray-a12'),
								textShadow: '1px 1px 0px var(--text-shadow-color)',
								['--text-shadow-color']: `color(from ${color('gray-3')} display-p3 r g b / 0.7)`,

								...theme.applyStyles('light', {
									color: color('gray-1'),
									['--text-shadow-color']: `color(from ${color('gray-12')} display-p3 r g b / 0.3)`,
									['--delete-icon-alpha']: 0.8,

									'&.Mui-disabled': {
										['--text-shadow-color']: `color(from ${color('gray-3')} display-p3 r g b / 0.7)`,
									},
								}),
							},
						},

						{
							props: { variant: 'filled', color: 'primary' },
							style: {
								backgroundColor: color('blue-7'),
								border: `1px solid ${color('blue-a5')}`,
								['--chip-shadow-color']: `oklch(from ${color('blue-9')} l c h / 0.5)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: color('blue-8'),
								},

								...theme.applyStyles('light', {
									backgroundColor: color('blue-8'),
									border: `1px solid ${color('blue-a7')}`,

									'&.MuiChip-clickable:hover': {
										backgroundColor: color('blue-9'),
									},
								}),
							},
						},
						{
							props: { variant: 'filled', color: 'success' },
							style: {
								backgroundColor: color('green-a6'),
								border: `1px solid ${color('green-a3')}`,
								['--chip-shadow-color']: `oklch(from ${color('green-9')} l c h / 0.4)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: color('green-7'),
								},

								...theme.applyStyles('light', {
									backgroundColor: color('green-8'),
									border: `1px solid ${color('green-a7')}`,

									'&.MuiChip-clickable:hover': {
										backgroundColor: `oklch(from ${color('green-8')} calc(l * 0.94) c h)`,
									},
								}),
							},
						},
						{
							props: { variant: 'filled', color: 'warning' },
							style: {
								backgroundColor: `oklch(from ${color('yellow-11')} calc(l * 0.65) c h / 0.8)`,
								border: `1px solid ${color('yellow-a6')}`,
								['--chip-shadow-color']: `oklch(from ${color('yellow-9')} l c h / 0.3)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: `oklch(from ${color('yellow-9')} l c h / 0.5)`,
								},

								...theme.applyStyles('light', {
									backgroundColor: `oklch(from ${color('yellow-9')} l calc(c * 0.8) h)`,
									color: color('yellow-12'),
									border: `1px solid ${color('yellow-a6')}`,
									['--text-shadow-color']: `oklch(from ${color('gray-2')} l c h / 0.7)`,
									['--delete-icon-alpha']: 0.5,

									'&.MuiChip-clickable:hover': {
										backgroundColor: `oklch(from ${color('yellow-9')} calc(l * 0.97) c h)`,
									},
								}),
							},
						},
						{
							props: { variant: 'filled', color: 'error' },
							style: {
								backgroundColor: color('red-a4'),
								border: `1px solid ${color('red-a3')}`,
								['--chip-shadow-color']: `oklch(from ${color('red-9')} l c h / 0.5)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: color('red-a6'),
								},

								...theme.applyStyles('light', {
									backgroundColor: `oklch(from ${color('red-9')} calc(l * 1.15) c h)`,
									color: `oklch(from ${color('red-1')} l c h / 0.95)`,
									border: `1px solid ${color('red-a10')}`,

									'&.MuiChip-clickable:hover': {
										backgroundColor: `oklch(from ${color('red-9')} calc(l * 1.08) c h)`,
									},
								}),
							},
						},
						{
							props: { variant: 'filled', color: 'gray' },
							style: {
								backgroundColor: color('gray-a7'),
								border: `1px solid ${color('gray-a4')}`,
								['--chip-shadow-color']: `oklch(from ${color('gray-9')} l c h / 0.4)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: `oklch(from ${color('gray-a8')} calc(l * 0.9) c h)`,
								},

								...theme.applyStyles('light', {
									backgroundColor: color('gray-9'),
									border: `1px solid ${color('gray-a7')}`,
									['--text-shadow-color']: `color(from ${color('gray-12')} display-p3 r g b / 0.5)`,
									['--delete-icon-alpha']: 0.9,

									'&.MuiChip-clickable:hover': {
										backgroundColor: color('gray-a9'),
									},
								}),
							},
						},
						{
							props: { variant: 'filled', color: 'orange' },
							style: {
								backgroundColor: `oklch(from ${color('orange-9')} l c h / 0.5)`,
								border: `1px solid ${color('orange-a5')}`,
								['--chip-shadow-color']: `oklch(from ${color('orange-9')} l c h / 0.4)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: `oklch(from ${color('orange-9')} l c h / 0.6)`,
								},

								...theme.applyStyles('light', {
									backgroundColor: `oklch(from ${color('orange-10')} calc(l * 1.08) calc(c * 0.8) h)`,
									border: `1px solid ${color('orange-a10')}`,
									['--delete-icon-alpha']: 0.9,

									'&.MuiChip-clickable:hover': {
										backgroundColor: `oklch(from ${color('orange-9')} calc(l * 1.04) c h)`,
									},
								}),
							},
						},

						{
							props: { variant: 'outlined' },
							style: {
								textShadow: '1px 1px 0px var(--text-shadow-color)',
								['--text-shadow-color']: `oklch(from ${color('gray-1')} l c h / 0.8)`,

								...theme.applyStyles('light', {
									['--text-shadow-color']: `oklch(from ${color('gray-1')} l c h / 0.8)`,
									['--delete-icon-alpha']: 0.65,
								}),
							},
						},
						{
							props: { variant: 'outlined', color: 'primary' },
							style: {
								backgroundColor: `oklch(from ${color('blue-3')} calc(l * 1.02) calc(c * 0.7) h / 0.9)`,
								color: color('blue-11'),
								borderColor: color('blue-a6'),
								['--chip-shadow-color']: `oklch(from ${color('blue-9')} l c h / 0.5)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: `oklch(from ${color('blue-3')} l c h / 0.9)`,
									borderColor: color('blue-a7'),
								},

								...theme.applyStyles('light', {
									'&.MuiChip-clickable:hover': {
										backgroundColor: `oklch(from ${color('blue-4')} l c h / 0.9)`,
										borderColor: color('blue-8'),
									},
								}),
							},
						},
						{
							props: { variant: 'outlined', color: 'success' },
							style: {
								backgroundColor: `oklch(from ${color('green-3')} calc(l * 1.02) c h)`,
								color: color('green-11'),
								borderColor: color('green-a6'),
								['--chip-shadow-color']: `oklch(from ${color('green-9')} l c h / 0.4)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: color('green-4'),
									borderColor: color('green-a8'),
								},

								...theme.applyStyles('light', {
									borderColor: color('green-a7'),
								}),
							},
						},
						{
							props: { variant: 'outlined', color: 'warning' },
							style: {
								backgroundColor: `oklch(from ${color('yellow-a3')} l c h / calc(alpha * 0.8))`,
								color: color('yellow-11'),
								borderColor: color('yellow-a6'),
								['--chip-shadow-color']: `oklch(from ${color('yellow-9')} l c h / 0.3)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: color('yellow-a4'),
									borderColor: color('yellow-a7'),
								},

								...theme.applyStyles('light', {
									borderColor: color('yellow-a7'),
									['--delete-icon-alpha']: 0.8,

									'&.MuiChip-clickable:hover': {
										borderColor: color('yellow-8'),
									},
								}),
							},
						},
						{
							props: { variant: 'outlined', color: 'error' },
							style: {
								backgroundColor: color('red-a2'),
								color: `oklch(from ${color('red-a12')} calc(l * 0.85) calc(c * 2.5) h / alpha)`,
								borderColor: `oklch(from ${color('red-a6')} l c h / calc(alpha * 0.9))`,
								['--chip-shadow-color']: `oklch(from ${color('red-9')} l c h / 0.6)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: `oklch(from ${color('red-a3')} l c h / calc(alpha * 1))`,
									borderColor: color('red-7'),
								},

								...theme.applyStyles('light', {
									backgroundColor: `oklch(from ${color('red-5')} calc(l * 1.15) c h)`,
									borderColor: `oklch(from ${color('red-a8')} l c h / calc(alpha * 0.9))`,

									'&.MuiChip-clickable:hover': {
										backgroundColor: `oklch(from ${color('red-4')} calc(l) c h)`,
										borderColor: `oklch(from ${color('red-8')} calc(l * 0.95) c h)`,
									},
								}),
							},
						},
						{
							props: { variant: 'outlined', color: 'gray' },
							style: {
								backgroundColor: `oklch(from ${color('gray-6')} l c h / 0.6)`,
								color: `oklch(from ${color('gray-a12')} l c h / calc(alpha * 0.8))`,
								borderColor: color('gray-a6'),
								['--chip-shadow-color']: `oklch(from ${color('gray-9')} l c h / 0.4)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: color('gray-a5'),
									borderColor: color('gray-a8'),
								},

								...theme.applyStyles('light', {
									backgroundColor: `oklch(from ${color('gray-4')} calc(l * 1.01) c h / 0.7)`,
									borderColor: color('gray-a7'),
									['--delete-icon-alpha']: 0.45,
								}),
							},
						},
						{
							props: { variant: 'outlined', color: 'orange' },
							style: {
								backgroundColor: color('orange-a3'),
								color: color('orange-a11'),
								borderColor: color('orange-a6'),
								['--chip-shadow-color']: `oklch(from ${color('orange-9')} l c h / 0.4)`,

								'&.MuiChip-clickable:hover': {
									backgroundColor: color('orange-a4'),
									borderColor: color('orange-a7'),
								},

								...theme.applyStyles('light', {
									'&:hover': {
										backgroundColor: `oklch(from ${color('orange-a4')} l c h / calc(alpha * 0.8))`,
										borderColor: color('orange-a8'),
										['--delete-icon-alpha']: 0.7,
									},
								}),
							},
						},
					],
				}),

				deleteIcon: {
					fontSize: 'inherit',
					margin: spacing(0.25), // The delete icon's height eats into spacing(1.5) of the chip's padding, so spacing(0.5) is what's left on each side
					width: `calc(1cap + ${spacing(1.5)})`,
					height: `calc(1cap + ${spacing(1.5)})`,
					color: 'oklch(from currentcolor l c h / var(--delete-icon-alpha, 0.6))',
					filter: 'drop-shadow(1px 1px 0px var(--text-shadow-color))',

					'&:hover': {
						color: 'oklch(from currentcolor l c h / var(--delete-icon-alpha, 0.6) * 1.2)',
					},
				},

				icon: {
					marginRight: '0px',
				},

				label: {
					padding: `0rem ${spacing(1.25)}`,
					overflow: 'revert',

					'.MuiChip-deletable > &': {
						paddingRight: spacing(0.25),
					},
				},
			},
		},

		MuiDataGrid: {
			defaultProps: {
				// The rows have a border-bottom of 1px, so I give the rows a height of <cell height> + 1px
				columnHeaderHeight: 23,
				rowHeight: 29,

				hideFooter: true,

				slotProps: {
					// Targets at least the "Reset" button in the `GridColumnsManagementFooter` component. Not sure if it targets other buttons.
					baseButton: {
						size: 'small',
					},
				},
			},
			styleOverrides: {
				root: {
					borderRadius: 'var(--radius-lg)', // For Tailwind class detection: rounded-lg

					'& .MuiDataGrid-row:hover': {
						backgroundColor: dataGridRowHoverColor,
					},

					// Mimic MUI's default selectors to match their specificity
					'& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row:hover': {
						'& .MuiDataGrid-cell--pinnedLeft': {
							backgroundColor: dataGridRowHoverColor,
						},

						'& .MuiDataGrid-cell--pinnedRight': {
							backgroundColor: dataGridRowHoverColor,
						},
					},

					'.MuiDataGrid-columnHeader--filter': {
						padding: `${spacing(1.5)} ${spacing(1.5)}`,
					},

					'& .MuiDataGrid-booleanCell > path': {
						strokeWidth: '2px',
						stroke: 'currentColor',
					},

					'& .MuiDataGrid-booleanCell[data-value="true"]': {
						color: color('green-9'),
					},

					// MUI centres its resize glyph in every column separator, whether or not that edge can be
					// dragged: on one that cannot it promises something that does not happen, and on one that can
					// it sits at a 13% alpha, findable only by hunting along the heading for a cursor change. A
					// line stands in for it either way — dotted where the edge drags, plain where it only divides.
					'& .MuiDataGrid-columnSeparator': {
						// The glyph was what gave the separator its width, so the grab target has to be stated once
						// it is gone, or the separator collapses onto the 1px line and is near-impossible to hit.
						// Kept at the 10px MUI positions for, since the offset that centres it is MUI's own.
						width: '10px',
						minWidth: '10px',

						'& .MuiDataGrid-iconSeparator': {
							display: 'none',
						},

						// The line is a background rather than a bordered child: the separator straddles the
						// boundary it belongs to, so centring a background sizes it onto that edge exactly, and it
						// takes no extra element to place.
						backgroundImage: `linear-gradient(to bottom, ${color('gray-a5')}, ${color('gray-a5')})`,
						backgroundSize: '1px 100%',
						backgroundPosition: 'center',
						backgroundRepeat: 'no-repeat',
					},

					// Dotted, and darker, for an edge that can actually be dragged — the affordance the threshold
					// table draws for itself, so a resizable edge reads the same wherever it appears.
					'& .MuiDataGrid-columnSeparator--resizable': {
						backgroundImage: `repeating-linear-gradient(to bottom, ${color('gray-a7')} 0 2px, transparent 2px 4px)`,

						// Lit while dragging as well as on hover: a drag keeps going once the pointer has left the
						// separator behind, and the edge being moved should stay the one that looks live.
						'&:hover, &.MuiDataGrid-columnSeparator--resizing': {
							backgroundImage: `linear-gradient(to bottom, ${color('gray-a11')}, ${color('gray-a11')})`,
						},
					},
				},

				cell: {
					'&.Mui-selected': {
						backgroundColor: color('blue-4'),

						'&:hover': {
							backgroundColor: color('blue-5'),
						},
					},

					'&:focus-within': {
						outlineColor: color('blue-a8'),
					},
				},

				'cell--editing': {
					padding: '0rem !important',
				},

				columnHeaderFilterInput: {
					'& .MuiInputBase-input': {
						fontSize: 'unset',
					},
				},

				columnHeaderTitleContainer: {
					gap: spacing(2),
				},

				columnsManagement: {
					padding: `${spacing(1.5)} ${spacing(3)}`,
					paddingBottom: spacing(2.5),
					gap: spacing(2.5),

					// The `columnsManagementRow` style override wasn't working so I had to target the element through CSS
					'& > .MuiDataGrid-columnsManagementRow': {
						gap: spacing(2),
						overflow: 'unset',

						'& > .MuiCheckbox-root': {
							padding: spacing(2),
						},

						'& > .MuiTypography-root.MuiFormControlLabel-label': {
							padding: `${spacing(0.75)} ${spacing(0)}`,
							margin: `${spacing(-0.75)} ${spacing(0)}`,
							fontSize: 'var(--text-base)',
						},
					},
				},

				columnsManagementFooter: {
					padding: `${spacing(1.5)} ${spacing(3)}`,

					'& > .MuiFormControlLabel-root': {
						gap: spacing(2),
						overflow: 'unset',

						'& > .MuiCheckbox-root': {
							padding: spacing(2),
						},

						'& > .MuiTypography-root.MuiFormControlLabel-label': {
							padding: `${spacing(0.75)} ${spacing(0)}`,
							margin: `${spacing(-0.75)} ${spacing(0)}`,
							fontSize: 'var(--text-base)',
						},
					},
				},

				columnsManagementSearchInput: {
					'.MuiInputAdornment-root': {
						margin: '0',
					},
				},

				filterForm: {
					paddingTop: spacing(1.75),

					'div:has(> &)': {
						padding: `${spacing(1.5)} ${spacing(3)}`,
						paddingBottom: spacing(2.5),
					},
				},

				iconButtonContainer: {
					fontSize: 'var(--text-sm)',
				},

				main: {
					outline: 'none',
				},

				menu: {
					'& > div.MuiPaper-root': {
						backgroundColor: translucentPaperStyles.backgroundColor,
						backdropFilter: translucentPaperStyles.backdropFilter,
						border: `1px solid ${color('gray-a6')}`,
						color: color('gray-a12'),
					},
				},

				panel: {
					'& > div > div.MuiDataGrid-paper': {
						backgroundColor: translucentPaperStyles.backgroundColor,
						backdropFilter: translucentPaperStyles.backdropFilter,
						border: `1px solid ${color('gray-a6')}`,
						color: color('gray-a12'),
					},
				},

				panelFooter: {
					padding: spacing(2),
					gap: spacing(2.5),

					'& > button': {
						paddingTop: spacing(0.5),
						paddingBottom: spacing(0.5),
					},
				},

				row: {
					color: `color-mix(in oklab, ${color('gray-a12')}, transparent 10%)`, // Soften the text colours a bit
					transition: 'background-color 50ms',

					'&.Mui-selected': {
						backgroundColor: color('blue-4'),

						'&:hover': {
							backgroundColor: color('blue-5'),
						},

						'& > .MuiDataGrid-cell--pinnedLeft': {
							backgroundColor: `${color('blue-4')} !important`,
						},
					},
				},

				toolbar: {
					minHeight: 'unset',
					padding: spacing(0.5),
					gap: spacing(2),
				},
			},
		},

		MuiDialog: {
			styleOverrides: {
				paper: {
					borderRadius: 'var(--radius-xl)', // For Tailwind class detection: rounded-xl
				},
			},
		},

		MuiDrawer: {
			styleOverrides: {
				paperAnchorLeft: {
					borderWidth: '0px 1px 0px 0px',
				},

				paperAnchorRight: {
					borderWidth: '0px 0px 0px 1px',
				},

				paperAnchorTop: {
					borderWidth: '1px 0px 0px 0px',
				},

				paperAnchorBottom: {
					borderWidth: '0px 0px 1px 0px',
				},
			},
		},

		MuiFormControlLabel: {
			styleOverrides: {
				root: {
					marginLeft: '0rem',
					marginRight: '0rem',
					gap: spacing(1),
				},

				label: {
					textBox: 'trim-both cap alphabetic',
				},
			},
		},

		MuiFormLabel: {
			styleOverrides: {
				root: {
					textBox: 'revert',
					lineHeight: 'revert',
				},
			},
		},

		MuiIconButton: {
			styleOverrides: {
				root: {
					fontSize: 'var(--text-base)',
					padding: spacing(0),

					'&:hover': {
						backgroundColor: 'transparent',
					},

					'::after': {
						content: '""',
						position: 'absolute',
						inset: spacing(-1),
						borderRadius: '50%',
					},

					'&:hover::after': {
						backgroundColor: color('gray-a3'),
					},

					'& > span.MuiTouchRipple-root': {
						inset: spacing(-1),
					},
				},
			},
		},

		MuiInput: {
			styleOverrides: {
				input: {
					paddingTop: spacing(2),
					paddingBottom: spacing(2),
				},
			},
		},

		MuiInputBase: {
			styleOverrides: {
				input: {
					'&::placeholder': {
						color: color('gray-a8'),
						opacity: 1,
					},
				},
			},
		},

		MuiInputLabel: {
			styleOverrides: {
				root: {
					// Match how <input> text doesn't have a text-box trim. MUI layers this component on top of the input, so the position must match.
					textBox: 'revert',
					lineHeight: 'revert',
					color: color('gray-11'),

					transform: `translate(${inputPaddingX}, ${inputPaddingY}) scale(1)`,

					'&.Mui-focused': {
						color: color('blue-11'),
					},
				},

				asterisk: {
					color: color('red-a8'),
				},
				shrink: {
					// `translate()` x bumps the label to match `inputPaddingX` (minus 1px because the input has a 1px border)
					// `translate()` y bumps the label up to sit flush with the `notchedOutline` border (the value was eyeballed)
					// `scale()` takes 11px (--text-base) down to 9px (--text-xs) (unfortunate fraction)
					transform: `translate(calc(${inputPaddingX} - 1px), -0.8em) scale(${9 / 11})`,
				},
			},
		},

		MuiLinearProgress: {
			styleOverrides: {
				root: {
					border: `1px solid oklch(from ${color('blue-10')} l c h / 0.3)`,
					backgroundColor: color('blue-2'),
				},

				bar: {
					borderRadius: '100px',
					backgroundImage: `linear-gradient(0deg, ${color('blue-6')}, ${color('blue-8')})`,
				},
			},
		},

		MuiLink: {
			styleOverrides: {
				root: {
					textDecoration: 'none',
				},
			},
		},

		MuiList: {
			styleOverrides: {
				root: {
					paddingTop: spacing(1),
					paddingBottom: spacing(1),
				},
			},
		},

		MuiListItemIcon: {
			styleOverrides: {
				root: {
					height: '0px', // Make the icon not contribute to the button's height
					alignItems: 'center',
				},
			},
		},

		MuiMenuItem: {
			styleOverrides: {
				root: {
					paddingTop: spacing(2.5),
					paddingBottom: spacing(2.5),
					paddingLeft: spacing(2),
					paddingRight: spacing(2),
					transition: 'background-color 50ms',
					minHeight: '1cap',
					gap: spacing(2),

					'&.Mui-selected': {
						backgroundColor: color('gray-a4'),

						'&:hover': {
							backgroundColor: color('gray-a4'),
						},

						'&.Mui-focusVisible': {
							backgroundColor: color('blue-a5'),
						},
					},

					'.MuiListItemIcon-root': {
						minWidth: '0px',
					},
				},
			},
		},

		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					'&.Mui-disabled': {
						backgroundColor: color('gray-a1'),
					},

					'&:hover .MuiOutlinedInput-notchedOutline': {
						borderColor: color('gray-a8'),
					},

					'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
						borderColor: color('blue-9'),
					},

					// After the hover and focus rules so an invalid field stays marked while it is being
					// corrected. Without this the notchedOutline override below wins and `error` paints nothing.
					'&.Mui-error .MuiOutlinedInput-notchedOutline': {
						borderColor: color('red-9'),
					},
				},

				adornedEnd: {
					paddingRight: inputPaddingX,
				},
				input: {
					padding: `${inputPaddingY} ${inputPaddingX}`,
				},
				multiline: {
					padding: '0rem',
				},
				notchedOutline: {
					// Compensate for:
					// - 2px border
					// - 5px <span> padding
					padding: `0rem calc(${inputPaddingX} - 2px - 5px)`,
					borderColor: color('gray-a7'),
					transition: 'border-width 50ms, border-color 100ms',

					'& > legend': {
						fontSize: 'var(--text-xs)',
					},
				},
			},
		},

		MuiPaper: {
			styleOverrides: {
				root: {
					// https://mui.com/material-ui/react-paper/#elevation
					backgroundImage: 'none',

					border: `1px solid ${color('gray-6')}`,
				},
			},
		},

		MuiPickersOutlinedInput: {
			styleOverrides: {
				root: {
					padding: `${spacing(0)} ${inputPaddingX}`,
				},

				notchedOutline: {
					// Compensate for:
					// - 2px border
					// - 5px <span> padding
					padding: `0rem calc(${inputPaddingX} - 2px - 5px)`,
					borderColor: color('gray-a7'),
					transition: 'border-width 50ms, border-color 100ms',

					'& > legend': {
						fontSize: 'var(--text-xs)',
					},
				},

				sectionsContainer: {
					padding: `${inputPaddingY} ${spacing(0)}`,
				},
			},
		},

		MuiPickersSectionList: {
			styleOverrides: {
				section: {
					// Match how <input> text doesn't have a text-box trim. MUI layers this component on top of the input, so the position must match.
					textBox: 'revert',
					lineHeight: 'revert',
				},
			},
		},

		MuiPopover: {
			styleOverrides: {
				paper: {
					backgroundColor: translucentPaperStyles.backgroundColor,
					backdropFilter: translucentPaperStyles.backdropFilter,
				},
			},
		},

		MuiRadio: {
			styleOverrides: {
				root: {
					padding: spacing(1),
				},
			},
		},

		MuiSelect: {
			styleOverrides: {
				root: {
					'&::before': {
						borderBottomColor: color('gray-a7'),
					},

					':hover .MuiOutlinedInput-notchedOutline': {
						borderColor: color('gray-a8'),
					},
				},

				select: {
					minHeight: '1cap',
					paddingTop: inputPaddingY,
					paddingBottom: inputPaddingY,
				},
			},
		},

		MuiSwitch: {
			styleOverrides: {
				root: {
					width: spacing(7),
					height: spacing(3),
					padding: '0rem',
					margin: spacing(1),
					overflow: 'revert',

					// MUI's small-size styles are nested under the root class, which
					// out-specifies the switchBase/thumb slot overrides below — re-assert
					// the custom geometry at matching specificity so both sizes render
					// identically.
					'&.MuiSwitch-sizeSmall': {
						'& .MuiSwitch-switchBase': {
							padding: spacing(2),

							'&.Mui-checked': {
								transform: `translate(${spacing(3)}, -50%)`,
							},
						},

						'& .MuiSwitch-thumb': {
							width: spacing(4),
							height: spacing(4),
						},
					},
				},

				switchBase: {
					top: '50%',
					transform: 'translateY(-50%)',
					padding: spacing(2),
					left: spacing(-2),

					'&.Mui-checked': {
						transform: `translate(${spacing(3)}, -50%)`,
					},
				},
				thumb: {
					width: spacing(4),
					height: spacing(4),
				},
			},
		},

		MuiSvgIcon: {
			styleOverrides: {
				root: {
					fontSize: 'inherit',
					height: '1.25em',
					width: '1.25em',
				},
			},
		},

		MuiTab: {
			styleOverrides: {
				root: {
					padding: '0px',
					minHeight: spacing(10),
					fontWeight: 500,
				},
			},
		},

		MuiTabPanel: {
			styleOverrides: {
				root: {
					padding: 'revert',
				},
			},
		},

		MuiTableCell: {
			styleOverrides: {
				root: {
					padding: `${spacing(2)} ${spacing(3)}`,
					color: 'revert-layer',
				},

				head: {
					backgroundColor: color('gray-2'),
				},
			},
		},

		MuiTabs: {
			styleOverrides: {
				root: {
					minHeight: spacing(10),
				},
			},
		},

		MuiToggleButton: {
			styleOverrides: {
				root: {
					padding: `${buttonPaddingY} ${buttonPaddingX}`,
					color: color('gray-a11'),

					'&.Mui-selected': {
						color: color('blue-a12'),
						backgroundColor: color('blue-a3'),

						'&:hover': {
							backgroundColor: color('blue-a4'),
						},
					},
				},
			},
		},

		MuiTooltip: {
			defaultProps: {
				arrow: true,
				placement: 'top',
				enterDelay: 1000,

				// https://mui.com/material-ui/react-tooltip/#distance-from-anchor
				slotProps: {
					popper: {
						modifiers: [
							{
								name: 'offset',
								options: {
									offset: [0, 0],
								},
							},
						],
					},
				},
			},
			styleOverrides: {
				tooltip: {
					fontWeight: 'revert',
					backdropFilter: translucentPaperStyles.backdropFilter,
					border: `1px solid ${color('gray-a6')}`,
					padding: spacing(2),
					color: color('gray-a12'),
				},

				arrow: {
					'&::before': {
						border: `1px solid ${color('gray-a6')}`,
					},
				},

				// https://mui.com/material-ui/react-tooltip/#distance-from-anchor
				tooltipPlacementBottom: {
					marginTop: `${spacing(2)} !important`,
				},
				tooltipPlacementLeft: {
					marginRight: `${spacing(2)} !important`,
				},
				tooltipPlacementRight: {
					marginLeft: `${spacing(2)} !important`,
				},
				tooltipPlacementTop: {
					marginBottom: `${spacing(2)} !important`,
				},
			},
		},

		MuiTypography: {
			defaultProps: {
				variantMapping: {
					'2xs': 'p',
					xs: 'p',
					sm: 'p',
					base: 'p',
					lg: 'p',
					xl: 'p',
					'2xl': 'p',
					'3xl': 'p',
					'4xl': 'p',
				},
			},
		},
	},
});

const fallbackApplyStyles = theme.applyStyles;
// Override `theme.applyStyles` so that it uses the `@container` method to determine the colour scheme, same as
// in `app.css`.
theme.applyStyles = function applyStyles(this: Theme, key: 'light' | 'dark', styles: CSSObject): CSSObject {
	if (!this.colorSchemes[key]) {
		return fallbackApplyStyles.call(this, key, styles);
	}
	return {
		// 1. The element declares the scheme itself.
		[`&:where([data-color-scheme='${key}'])`]: styles,
		// 2. The nearest declaring ancestor is `key`, and this element declares nothing itself.
		[`@container style(--color-scheme: ${key})`]: {
			'&:where(:not([data-color-scheme]))': styles,
		},
	};
};

declare module '@mui/material/styles' {
	// extends MUI palette with our custom colours
	interface Palette {
		gray: Palette['primary'];
		orange: Palette['primary'];
	}

	interface PaletteOptions {
		gray?: PaletteOptions['primary'];
		orange?: PaletteOptions['primary'];
	}

	interface TypographyVariants {
		'2xs': CSSProperties;
		xs: CSSProperties;
		sm: CSSProperties;
		base: CSSProperties;
		lg: CSSProperties;
		xl: CSSProperties;
		'2xl': CSSProperties;
		'3xl': CSSProperties;
		'4xl': CSSProperties;
	}

	interface TypographyVariantsOptions {
		'2xs': CSSProperties;
		xs: CSSProperties;
		sm?: CSSProperties;
		base?: CSSProperties;
		lg?: CSSProperties;
		xl?: CSSProperties;
		'2xl'?: CSSProperties;
		'3xl'?: CSSProperties;
		'4xl'?: CSSProperties;
	}

	// Manually copy over theme augmentation from `@mui/x-data-grid/themeAugmentation` because of this bug: https://github.com/mui/mui-x/issues/15224
	interface PaletteDataGrid {
		bg?: string;
		headerBg?: string;
		pinnedBg?: string;
	}

	interface CssVarsPalette {
		DataGrid: PaletteDataGrid;
	}

	interface PaletteOptions {
		DataGrid?: Partial<PaletteDataGrid>;
	}
}

declare module '@mui/material/Chip' {
	interface ChipPropsColorOverrides {
		gray: true;
		orange: true;

		// Unset the default colours we don't want (commented-out lines are tokens which are still being used in the app, which I have to migrate away from):
		default: false;
		secondary: false;
		info: false;
	}
}

declare module '@mui/material/Button' {
	interface ButtonPropsColorOverrides {
		gray: true;
		orange: true;
	}
}

declare module '@mui/material/IconButton' {
	interface IconButtonPropsColorOverrides {
		gray: true;
		orange: true;
	}
}

declare module '@mui/material/Typography' {
	interface TypographyPropsVariantOverrides {
		h1: false;
		h2: false;
		h3: false;
		h4: false;
		h5: false;
		h6: false;
		subtitle1: false;
		subtitle2: false;
		body1: false;
		body2: false;
		button: false;
		caption: false;
		overline: false;
		'2xs': true;
		xs: true;
		sm: true;
		base: true;
		lg: true;
		xl: true;
		'2xl': true;
		'3xl': true;
		'4xl': true;
	}
}
